import { FC, memo, useCallback, useMemo, ReactNode } from 'react'
import styles from 'components/features/insights/InsightPostAttach/InsightPostAttach.styl'
import { InsightPostNormalized } from 'interfaces/feed'
import { Markdown } from 'components/features/pfm/Markdown/Markdown'
import InsightFooterContainer from 'components/features/insights/InsightFooter/InsightFooterContainer'
import { InsightPostCTALink } from 'components/features/insights/InsightPostCTALink/InsightPostCTALink'
import { createImageTokenRenderer, sendWarning, createVideoTokenRenderer } from 'helpers/insightMarkdownHelper'
import { ImageMarkdownNode } from 'marked-ast'

type InsightPostAttach = {
  post: InsightPostNormalized
}

export const InsightPostAttach: FC<InsightPostAttach> = memo(({
  post: {
    insight,
    uid,
  },
}) => {
  const { text } = insight

  const handleError = useCallback((tokenType) => sendWarning(insight, tokenType), [insight])
  const imageRenderer = useMemo(() => createImageTokenRenderer(handleError), [handleError])
  const videoRenderer = useMemo(() => createVideoTokenRenderer(handleError), [handleError])

  const handleToken = useCallback<(token: ImageMarkdownNode, key: string) => ReactNode>((token, key) => (
    <div key={ key } className={ styles.insightPostAttach } data-type={ token.type }>
      <div className={ styles.media }>
        { token.type === 'video' && videoRenderer(token, key) }
        { token.type === 'image' && (
          <InsightPostCTALink postUid={ uid }>
            { imageRenderer(token, key) }
          </InsightPostCTALink>
        )}
      </div>
      <div className={ styles.footer }>
        <div className={ styles.title }>
          <InsightPostCTALink postUid={ uid }>
            { insight.title }
          </InsightPostCTALink>
        </div>
        <div className={ styles.actions }>
          <InsightFooterContainer insight={ insight } />
        </div>
      </div>
    </div>
  ), [imageRenderer, insight, uid, videoRenderer])

  return (
    <div className={ styles.text }>
      <Markdown
        text={ text }
        onImage={ handleToken }
        onVideo={ handleToken }
      />
    </div>
  )
})
